'use client';

import {
  type ComponentProps,
  createContext,
  type ReactNode,
  useContext,
  useId,
} from 'react';

import { tv } from '../../lib/tv';
import {
  RadioGroup as RadioGroupPrimitive,
  RadioGroupItem as RadioPrimitive,
} from '../ui/radio-group';
import { useFieldControl, useFieldLabelId } from './field';

export type RadioGroupProps = ComponentProps<typeof RadioGroupPrimitive> & {
  /** Stack the options (default) or lay them out in a wrapping row. */
  orientation?: 'vertical' | 'horizontal';
};

export type RadioProps = ComponentProps<typeof RadioPrimitive> & {
  /** Figma "Label" - clickable. Omit only when a label is provided elsewhere (aria-label). */
  label?: ReactNode;
};

/** A disabled group disables its options - their labels need to know too. */
const GroupDisabled = createContext(false);

const radio = tv({
  slots: {
    group: '',
    root: 'inline-flex items-center gap-2',
    label:
      'text-body-md text-text-primary peer-data-disabled:text-text-disabled cursor-pointer peer-data-disabled:cursor-not-allowed',
  },
  variants: {
    orientation: {
      vertical: { group: 'flex flex-col gap-3' },
      horizontal: { group: 'flex flex-row flex-wrap gap-x-6 gap-y-3' },
    },
  },
});

/**
 * Single choice from 2–6 visible options (Figma: Components ↳ Controls ↳
 * Radio). For more options use a Select. Arrow keys move the selection.
 * Inside a `Field`, the Field's label names the group.
 */
export function RadioGroup({
  orientation = 'vertical',
  className,
  ...props
}: RadioGroupProps) {
  const fieldLabelId = useFieldLabelId();
  const controlProps = useFieldControl(props);
  return (
    <GroupDisabled value={Boolean(controlProps.disabled)}>
      <RadioGroupPrimitive
        aria-labelledby={props['aria-label'] ? undefined : fieldLabelId}
        orientation={orientation}
        className={radio({ orientation }).group({ className })}
        {...controlProps}
      />
    </GroupDisabled>
  );
}

/** One option of a RadioGroup: the Figma circle plus its label. */
export function Radio({ label, id, className, ...props }: RadioProps) {
  const generated = useId();
  const groupDisabled = useContext(GroupDisabled);
  const styles = radio();
  if (!label) return <RadioPrimitive id={id} className={className} {...props} />;
  const controlId = id ?? generated;
  return (
    <span data-slot="radio-field" className={styles.root({ className })}>
      <RadioPrimitive id={controlId} {...props} />
      <label
        htmlFor={controlId}
        className={styles.label()}
        data-disabled={props.disabled || groupDisabled || undefined}
      >
        {label}
      </label>
    </span>
  );
}
